/* eslint-disable jsx-a11y/prefer-tag-over-role */
import React, { FormEvent, useEffect, useRef, useState } from "react";
import ct from "class-types.macro";
import { NumericFormat } from "react-number-format";
import sum from "lodash/sum";
import { useReCaptcha, RecaptchaDisclaimer } from "@wss/re-captcha";
import { getPhoneNumberType } from "../../phoneValidation";
import ContinueButton from "../../Buttons/ContinueButton";
import GoBackButton from "../../Buttons/GoBackButton";
import formatPhoneNumber from "../../formatPhoneNumber";
import { validateEmailAddress } from "../../../../../common/validations";
import { Answer } from "../../Step";

export interface DefaultFormData {
  contactId: number;
  name: string;
  email: string;
  phone: string;
}

export interface ContactFormData {
  contactId: number;
  name: string;
  email: string;
  phone: string;
  orderNumber: string;
  itemNumber: string;
  caseNumber: string;
  message: string;
  recaptchaToken: string | null;
  imagesUpload: File[] | [];
  answers: Answer[];
}

interface Props {
  defaultFormData: DefaultFormData;
  isFileUploadForm?: boolean;
  goBack: () => void;
  submitContactForm: (form: ContactFormData) => Promise<void>;
  answers: Answer[];
}

type ErrorPair = {
  inputName: string;
  inputId: string;
};

const pushIfExists = (target: ErrorPair[], value: ErrorPair | null) => {
  if (value) {
    target.push(value);
  }
};

const validateInteger = (numberToValidate: string) => {
  const num = Number(numberToValidate);
  if (Number.isNaN(num)) {
    return false;
  }
  return num > 0 && num <= 0x7fffffff;
};

const validateLongInteger = (numberToValidate: string) => {
  const num = Number(numberToValidate);
  if (Number.isNaN(num)) {
    return false;
  }
  return num > 0 && num <= 0x7fffffffffffffffn;
};

const isValidCaseNumber = (caseNumber: string) => {
  return caseNumber.length >= 12 && validateLongInteger(caseNumber);
};

const GeneralContactForm = ({
  defaultFormData,
  isFileUploadForm = false,
  goBack,
  submitContactForm,
  answers,
}: Props) => {
  const [formData, setFormData] = useState<ContactFormData>({
    ...defaultFormData,
    phone: formatPhoneNumber(defaultFormData.phone) ?? "",
    orderNumber: "",
    itemNumber: "",
    caseNumber: "",
    message: "",
    recaptchaToken: null,
    imagesUpload: [],
    answers,
  });
  const [nameError, setNameError] = useState(false);
  const [nameLength, setNameLength] = useState<number>(0);
  const [emailError, setEmailError] = useState(false);
  const [emailLength, setEmailLength] = useState<number>(0);
  const [phoneError, setPhoneError] = useState(false);
  const [messageLength, setMessageLength] = useState<number>(0);
  const [messageError, setMessageError] = useState(false);
  const [orderNumberError, setOrderNumberError] = useState(false);

  const [itemNumberError, setItemNumberError] = useState(false);

  const [caseNumberError, setCaseNumberError] = useState(false);
  const [caseNumberLength, setCaseNumberLength] = useState<number>(0);
  const [fileError, setFileError] = useState(false);
  const [intlPhoneError, setIntlPhoneError] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState<boolean>(
    false
  );
  const formRef = useRef<HTMLFormElement>(null);
  const reCaptcha = useReCaptcha();

  const orderNumberRequired =
    [1, 2, 3].indexOf(
      formData.answers.filter((v) => v.questionId === 1).slice(-1)[0]
        ?.answerId ?? -1
    ) > -1;
  const caseNumberRequired =
    formData.answers.filter((v) => v.questionId === 1).slice(-1)[0]
      ?.answerId === 4;
  const itemNumberMaxLength = 24;
  const itemNumberRequired = (formData.answers[0]?.answerId ?? 0) === 5;

  const errors: { inputName: string; inputId: string }[] = [];
  const handleChange = (
    event: React.ChangeEvent<
      HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
    >
  ) => {
    let valueToBeSet = event.target.value;
    if (event.target.name === "phone") {
      valueToBeSet = formatPhoneNumber(event.target.value);
      const trimmedValue = valueToBeSet.replace(/[^\d+]/g, "");
      if (trimmedValue.length < 1 || trimmedValue.length === 10) {
        setPhoneError(false);
        setIntlPhoneError(false);
      } else if (trimmedValue.length < 10) {
        setPhoneError(true);
        setIntlPhoneError(false);
      } else {
        if (trimmedValue.includes("+")) {
          setIntlPhoneError(false);
        } else {
          setIntlPhoneError(true);
        }
        setPhoneError(false);
      }
    }
    setFormData({
      ...formData,
      [event.target.name]: valueToBeSet,
    });
  };

  const onUploadBtnClick = () => {
    if (document.getElementById("imagesUpload")) {
      document.getElementById("imagesUpload")?.click();
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    setFileError(false);
    let totalFileSize = sum(formData.imagesUpload.map((i) => i.size));
    for (const file of e.target.files) {
      totalFileSize += file.size;
      if (
        formData.imagesUpload.some((f) => f.name === file.name) ||
        totalFileSize > 25000000 ||
        file.type !== "image/jpeg"
      ) {
        setFileError(true);
        return;
      }
    }
    for (const file of e.target.files) {
      setFormData({
        ...formData,
        imagesUpload: [...formData.imagesUpload, file],
      });
    }
  };

  const removeFile = (file: File) => {
    let files = formData.imagesUpload;
    files = files.filter((item: File) => item.name !== file.name);
    setFormData({ ...formData, imagesUpload: files });

    if (document.getElementById("gcf-upload-btn")) {
      document.getElementById("gcf-upload-btn")?.focus();
    }
  };

  const doValidateName = (name: string) => {
    setNameLength(name.length);
    if (name.length > 50 || name.length === 0) {
      setNameError(true);
      return { inputName: "name", inputId: "gcf-name" };
    }
    setNameError(false);
    return null;
  };

  const doValidateEmail = (email: string) => {
    setEmailLength(email.length);
    if (
      email.length === 0 ||
      email.length > 100 ||
      !validateEmailAddress(email)
    ) {
      setEmailError(true);
      return { inputName: "email", inputId: "gcf-email" };
    }
    setEmailError(false);
    return null;
  };

  const doValidatePhoneNumber = (phoneNumber: string) => {
    const trimLength = phoneNumber.replace(/[^\d+]/g, "").length;
    const targetPhoneNumber = formatPhoneNumber(phoneNumber);

    const phoneNumberType = getPhoneNumberType(targetPhoneNumber);
    if (phoneNumberType < 0) {
      if (trimLength > 10 || phoneNumber.startsWith("+")) {
        setPhoneError(false);
        setIntlPhoneError(true);
      } else {
        setPhoneError(true);
        setIntlPhoneError(false);
      }
      return { inputName: "phone", inputId: "gcf-phone" };
    }

    setPhoneError(false);
    setIntlPhoneError(false);
    return null;
  };

  const doValidateCaseNumber = (caseNumber: string) => {
    setCaseNumberLength(caseNumber.length);
    if (
      (caseNumberRequired && caseNumber.length === 0) ||
      (caseNumber.length > 0 && !isValidCaseNumber(caseNumber))
    ) {
      setCaseNumberError(true);
      return { inputName: "caseNumber", inputId: "gcf-caseNumber" };
    }
    setCaseNumberError(false);
    return null;
  };

  const doValidateMessage = (message: string) => {
    setMessageLength(message.length);
    if (message.length > 1024) {
      setMessageError(true);
      return { inputName: "message", inputId: "gcf-message" };
    }
    setMessageError(false);
    return null;
  };

  const doValidateOrderNumber = (orderNumber: string) => {
    if (
      (orderNumberRequired && orderNumber.length === 0) ||
      orderNumber.length > 10 ||
      (orderNumber.length > 0 && !validateInteger(orderNumber))
    ) {
      setOrderNumberError(true);
      return { inputName: "orderNumber", inputId: "gcf-orderNumber" };
    }

    setOrderNumberError(false);
    return null;
  };

  const doValidateItemNumber = (itemNumber: string) => {
    if (
      (itemNumberRequired && itemNumber.length === 0) ||
      itemNumber.length > itemNumberMaxLength
    ) {
      setItemNumberError(true);
      return { inputName: "itemNumber", inputId: "gcf-itemNumber" };
    }
    setItemNumberError(false);
    return null;
  };

  useEffect(() => {
    if (formData.name) {
      doValidateName(formData.name);
    }
  }, [formData.name]);

  useEffect(() => {
    if (formData.phone.length) {
      doValidatePhoneNumber(formData.phone);
    }
  }, [formData.phone]);

  useEffect(() => {
    if (formData.email) {
      doValidateEmail(formData.email);
    }
  }, [formData.email]);

  useEffect(() => {
    if (formData.orderNumber) {
      doValidateOrderNumber(formData.orderNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.orderNumber]);

  useEffect(() => {
    if (formData.caseNumber) {
      doValidateCaseNumber(formData.caseNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.caseNumber]);

  useEffect(() => {
    doValidateMessage(formData.message);
  }, [formData.message]);

  useEffect(() => {
    setHasValidationErrors(
      nameError ||
        emailError ||
        phoneError ||
        messageError ||
        itemNumberError ||
        caseNumberError
    );
  }, [
    nameError,
    emailError,
    phoneError,
    messageError,
    itemNumberError,
    caseNumberError,
  ]);

  useEffect(() => {
    if (formData.itemNumber) {
      doValidateItemNumber(formData.itemNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.itemNumber]);

  const validateAndSubmit = () => {
    pushIfExists(errors, doValidateName(formData.name));
    pushIfExists(errors, doValidatePhoneNumber(formData.phone));
    pushIfExists(errors, doValidateEmail(formData.email));
    pushIfExists(errors, doValidateCaseNumber(formData.caseNumber));
    pushIfExists(errors, doValidateMessage(formData.message));
    pushIfExists(errors, doValidateOrderNumber(formData.orderNumber));
    pushIfExists(errors, doValidateItemNumber(formData.itemNumber.trim()));

    const firstError = errors.find((e) => e !== undefined);
    if (firstError) {
      setHasValidationErrors(true);
      //  Accessibility: If there are errors in the form, focus will be forced to the first input with an error present
      document.getElementById(firstError.inputId)?.focus();
    } else {
      formRef?.current?.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true })
      );
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const recaptchaToken = await reCaptcha.execute();
    if (recaptchaToken.isError) throw recaptchaToken;
    await submitContactForm({
      ...formData,
      recaptchaToken: recaptchaToken.value,
    });
  };

  const ariaProp = (condition: boolean, value: string) => {
    if (condition) {
      return {
        "aria-describedby": value,
      };
    }
    return {};
  };

  const borderStyle = (condition: boolean) => {
    return condition
      ? "border-red-400 focus:border-red-400 focus:shadow-red"
      : "border-gray-200 focus:border-blue-300 focus:shadow-blue";
  };

  const errorMessageStyle = "text-red-600 font-normal m-0 text-xs";

  return (
    <div>
      {hasValidationErrors && (
        <div className="alert alert-info alert-info-dark">
          We need a few more details to help connect you. Please fill out all
          form fields to ask our team a question.
        </div>
      )}
      <div className="font-semibold leading-5">
        <form
          aria-label="Need to email customer solutions?"
          action="/api/support/getintouch/"
          onSubmit={handleSubmit}
          ref={formRef}
        >
          <input type="hidden" name="contactId" value={formData.contactId} />
          <label>
            Name * <br />
            <input
              id="gcf-name"
              autoComplete="name"
              aria-required
              required
              {...ariaProp(nameError, "gcf-name-error")}
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              className={ct(
                "appearance-none",
                "bg-white",
                "focus:bg-white",
                "bg-none",
                borderStyle(nameError),
                "rounded",
                "border-solid",
                "border",
                "box-border",
                "text-sm",
                "my-2",
                "outline-none",
                "py-4",
                "pl-3",
                "focus:placeholder-transparent",
                "relative",
                "w-full",
                "transition-all",
                "duration-200"
              )}
            />
            {nameError && (
              <p
                id="gcf-name-error"
                className="text-red-600 font-normal m-0 text-xs"
              >
                Name is required.
              </p>
            )}
            <div aria-live="polite">
              {nameLength > 50 && (
                <p id="gcf-name-error" className={errorMessageStyle}>
                  Name cannot exceed 50 characters.
                </p>
              )}
            </div>
          </label>
          <label>
            Email * <br />
            <input
              autoComplete="email"
              aria-required
              required
              {...ariaProp(emailError, "gcf-email-error")}
              id="gcf-email"
              type="email"
              value={formData.email}
              name="email"
              onChange={handleChange}
              className={ct(
                "appearance-none",
                "bg-white",
                "focus:bg-white",
                "bg-none",
                borderStyle(emailError),
                "rounded",
                "border-solid",
                "border",
                "box-border",
                "text-sm",
                "my-2",
                "outline-none",
                "py-4",
                "pl-3",
                "focus:placeholder-transparent",
                "relative",
                "w-full",
                "transition-all",
                "duration-200"
              )}
            />
            {emailError && (
              <p id="gcf-email-error" className={errorMessageStyle}>
                A valid email is required.
              </p>
            )}
            <div aria-live="polite">
              {emailLength > 100 && (
                <p id="gcf-email-error" className={errorMessageStyle}>
                  Email length cannot exceed 100 characters.
                </p>
              )}
            </div>
          </label>
          <label>
            Phone <br />
            <input
              autoComplete="tel"
              {...ariaProp(phoneError, "gcf-phone-error")}
              id="gcf-phone"
              maxLength={18}
              value={formData.phone}
              name="phone"
              onChange={handleChange}
              className={ct(
                "appearance-none",
                "bg-white",
                "focus:bg-white",
                "bg-none",
                borderStyle(phoneError),
                "rounded",
                "border-solid",
                "border",
                "box-border",
                "text-sm",
                "my-2",
                "outline-none",
                "py-2",
                "pl-3",
                "focus:placeholder-transparent",
                "relative",
                "w-full",
                "transition-all",
                "duration-200"
              )}
            />
            {!intlPhoneError && phoneError && (
              <p id="gcf-phone-error" className={errorMessageStyle}>
                Please enter a valid phone number.
              </p>
            )}
            {intlPhoneError && (
              <p className={errorMessageStyle}>
                Enter a + for international phone numbers.
              </p>
            )}
          </label>
          <label>
            Order Number {orderNumberRequired && "*"} <br />
            <NumericFormat
              type="tel"
              data-testId="orderNumber"
              value={formData.orderNumber}
              name="orderNumber"
              required={orderNumberRequired}
              onChange={handleChange}
              className={ct(
                "appearance-none",
                "bg-white",
                "focus:bg-white",
                "bg-none",
                borderStyle(orderNumberError),
                "rounded",
                "border-solid",
                "border",
                "box-border",
                "text-sm",
                "my-2",
                "outline-none",
                "py-4",
                "pl-3",
                "focus:placeholder-transparent",
                "relative",
                "w-full",
                "transition-all",
                "duration-200"
              )}
            />
            {orderNumberError &&
              orderNumberRequired &&
              formData.orderNumber.trim().length === 0 && (
                <p className={errorMessageStyle}>Order number is required.</p>
              )}
            {orderNumberError && formData.orderNumber.trim().length !== 0 && (
              <p className={errorMessageStyle}>
                A valid order number is required.
              </p>
            )}
          </label>
          <label>
            Item Number {itemNumberRequired ? "*" : ""} <br />
            <input
              type="text"
              value={formData.itemNumber}
              name="itemNumber"
              id="gcf-itemNumber"
              onChange={handleChange}
              className={ct(
                "appearance-none",
                "bg-white",
                "focus:bg-white",
                "bg-none",
                borderStyle(itemNumberError),
                "rounded",
                "border-solid",
                "border",
                "box-border",
                "text-sm",
                "my-2",
                "outline-none",
                "py-4",
                "pl-3",
                "focus:placeholder-transparent",
                "relative",
                "w-full",
                "transition-all",
                "duration-200"
              )}
            />
            {itemNumberError && formData.itemNumber.trim().length === 0 && (
              <p className="text-red-600 font-normal m-0 text-xs">
                Please enter an item number
              </p>
            )}
            {itemNumberError && formData.itemNumber.trim().length !== 0 && (
              <p className="text-red-600 font-normal m-0 text-xs">
                Item Number cannot exceed {itemNumberMaxLength} characters.
              </p>
            )}
          </label>
          {caseNumberRequired && (
            <label>
              Case Number * <br />
              <NumericFormat
                aria-required
                required
                {...ariaProp(caseNumberError, "gcf-caseNumber-error")}
                id="gcf-caseNumber"
                type="tel"
                value={formData.caseNumber}
                name="caseNumber"
                onChange={handleChange}
                className={ct(
                  "appearance-none",
                  "bg-white",
                  "focus:bg-white",
                  "bg-none",
                  borderStyle(caseNumberError),
                  "rounded",
                  "border-solid",
                  "border",
                  "box-border",
                  "text-sm",
                  "my-2",
                  "outline-none",
                  "py-4",
                  "pl-3",
                  "focus:placeholder-transparent",
                  "relative",
                  "w-full",
                  "transition-all",
                  "duration-200"
                )}
              />
              {caseNumberError && formData.caseNumber.trim().length !== 0 && (
                <p id="gcf-caseNumber-error" className={errorMessageStyle}>
                  A valid case number is required.
                </p>
              )}
              {caseNumberError && formData.caseNumber.trim().length === 0 && (
                <p className={errorMessageStyle}>Please enter a case number.</p>
              )}
              {caseNumberError && caseNumberLength < 12 && (
                <p className={errorMessageStyle}>
                  Case Number is at least 12 characters.
                </p>
              )}
              {caseNumberLength > 19 && (
                <p className={errorMessageStyle}>
                  Case Number cannot exceed 19 characters.
                </p>
              )}
            </label>
          )}
          <label>
            {isFileUploadForm
              ? "Please provide us with the item names, quantities, and details on any item issues."
              : "Please provide us with any additional details."}
            <br />
            <textarea
              id="gcf-message"
              name="message"
              data-testId="message"
              {...ariaProp(messageLength > 1024, "gcf-message-error")}
              value={formData.message}
              onChange={handleChange}
              style={{ resize: "none", height: "160px" }}
              className={ct(
                "appearance-none",
                "bg-white",
                "focus:bg-white",
                "bg-none",
                borderStyle(messageError),
                "rounded",
                "border-solid",
                "border",
                "box-border",
                "text-sm",
                "mt-2",
                "outline-none",
                "py-3",
                "pl-3",
                "focus:placeholder-transparent",
                "relative",
                "w-full",
                "transition-all",
                "duration-200"
              )}
            />
            <div aria-live="polite">
              {messageLength > 1024 && (
                <p id="gcf-message-error" className={errorMessageStyle}>
                  Message cannot exceed 1,024 characters.
                </p>
              )}
            </div>
          </label>

          {isFileUploadForm && (
            <>
              <p
                id="gcf-file-helper-1"
                className={ct("text-sm", "m-0", "mb-1")}
              >
                If items are damaged or incorrect, please attach your image(s).
              </p>
              <label
                aria-describedby={`${
                  fileError ? "gcf-file-error" : ""
                } gcf-file-helper-1 gcf-file-helper-2`}
              >
                <button
                  id="gcf-upload-btn"
                  type="button"
                  onClick={onUploadBtnClick}
                  className={ct(
                    "hover:bg-gray-100",
                    "border-gray-200",
                    "rounded",
                    "border-solid",
                    "border",
                    "cursor-pointer",
                    "inline-block",
                    "py-2",
                    "px-4",
                    "relative"
                  )}
                  aria-describedby={`${
                    fileError ? "gcf-file-error" : ""
                  } gcf-file-helper-1 gcf-file-helper-2`}
                >
                  <input
                    type="file"
                    id="imagesUpload"
                    name="imagesUpload"
                    accept=".jpg,.jpeg"
                    className={ct("hidden")}
                    onChange={handleUpload}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    className={ct("pr-1", "absolute")}
                    style={{ top: "19%", left: "13%" }}
                  >
                    <path fill="none" d="M0 0h24v24H0z" />
                    <path d="M4 19h16v-7h2v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-8h2v7zm9-10v7h-2V9H6l6-6 6 6h-5z" />
                  </svg>
                  <span className={ct("font-thin", "text-base", "pl-5")}>
                    Upload
                  </span>
                </button>
              </label>
              {formData.imagesUpload.map((file: File) => (
                <div
                  key={file.name}
                  className={ct("inline-block", "pt-4", "pr-5", "relative")}
                >
                  <span className={ct("cursor-pointer", "ml-3", "pr-2")}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      style={{ top: "42%" }}
                      className={ct(
                        "absolute",
                        "top-1/2",
                        "fill-current",
                        "text-gray-700"
                      )}
                      role="img"
                      aria-label="attachment"
                    >
                      <path fill="none" d="M0 0h24v24H0z" />
                      <path d="M14.828 7.757l-5.656 5.657a1 1 0 1 0 1.414 1.414l5.657-5.656A3 3 0 1 0 12 4.929l-5.657 5.657a5 5 0 1 0 7.071 7.07L19.071 12l1.414 1.414-5.657 5.657a7 7 0 1 1-9.9-9.9l5.658-5.656a5 5 0 0 1 7.07 7.07L12 16.244A3 3 0 1 1 7.757 12l5.657-5.657 1.414 1.414z" />
                    </svg>
                  </span>
                  <span
                    className={ct(
                      "font-thin",
                      "text-sm",
                      "pl-4",
                      "text-blue-600"
                    )}
                  >
                    {file.name}
                  </span>
                  <button
                    type="button"
                    aria-label={`remove ${file.name}`}
                    className={ct(
                      "cursor-pointer",
                      "ml-3",
                      "pr-2",
                      "bg-transparent",
                      "border-0"
                    )}
                    onClick={() => {
                      removeFile(file);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      className={ct("fill-current", "text-gray-700")}
                      style={{ top: "31%" }}
                    >
                      <path fill="none" d="M0 0h24v24H0z" />
                      <path d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z" />
                    </svg>
                  </button>
                </div>
              ))}
              <p
                id="gcf-file-helper-2"
                className={ct(
                  "font-semibold",
                  "text-xs",
                  "m-0",
                  "pt-2",
                  "text-gray-800"
                )}
              >
                .jpg files only, 25 MB maximum
              </p>
            </>
          )}
          {fileError && (
            <p
              id="gcf-file-error"
              aria-live="polite"
              className={errorMessageStyle}
            >
              One or more of the selected files are invalid.
            </p>
          )}
        </form>

        <div className={ct("flex")}>
          <GoBackButton backward={goBack} />
          <ContinueButton
            forward={validateAndSubmit}
            text="Contact Customer Solutions"
          />
        </div>
        <RecaptchaDisclaimer className="flex-initial text-gray-600 pt-4" />
      </div>
    </div>
  );
};

export default GeneralContactForm;
